import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Eye, CheckCircle, Clock, XCircle, AlertCircle, ChevronLeft, ChevronRight, Search, CreditCard, UserCircle, ShoppingBag, Package, Monitor, Truck, ShieldAlert, Wrench, AlertOctagon, MessageSquare, PackagePlus, HelpCircle, Loader2, ArrowUp, ArrowDown, Download } from 'lucide-react';
import { ticketAPI, authAPI } from '../../api';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import AccessModule from "../../components/AccessModule/AccessModule";

export default function TicketsPage() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [statusFilter, setStatusFilter] = useState('all');
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const statusDropdownRef = useRef<HTMLDivElement>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [nextAllowedTime, setNextAllowedTime] = useState<Date | null>(null);
  const [countdown, setCountdown] = useState<string | null>(null);
  const [ticketsLeft, setTicketsLeft] = useState<number>(10);

  useEffect(() => {
    let interval: any;
    if (nextAllowedTime) {
      interval = setInterval(() => {
        const now = new Date().getTime();
        const target = nextAllowedTime.getTime();
        const diff = target - now;
        
        if (diff <= 0) {
          setCountdown(null);
          setNextAllowedTime(null);
          clearInterval(interval);
        } else {
          const h = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
          const m = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
          const s = Math.floor((diff % (1000 * 60)) / 1000);
          setCountdown(`${h.toString().padStart(2, '0')}h ${m.toString().padStart(2, '0')}m ${s.toString().padStart(2, '0')}s`);
        }
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [nextAllowedTime]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(e.target as Node)) {
        setStatusDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const STATUS_OPTIONS = [
    { label: 'All Status', value: 'all' },
    { label: 'Open', value: 'Open' },
    { label: 'Inprogress', value: 'Inprogress' },
    { label: 'Resolved', value: 'Resolved' },
    { label: 'Closed', value: 'Closed' },
  ];
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');

  const [accessChecked, setAccessChecked] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState('');
  const [isBlocked, setIsBlocked] = useState(false);  

  useEffect(() => {
    const checkAccess = async () => {
      try {
        const userDetails = JSON.parse(
          localStorage.getItem("seller") || "{}"
        );
  
        const sellerRes = await authAPI.getSellerStatus(
          userDetails._id
        );
  
        setVerificationStatus(
          sellerRes.data.verification_status
        );
  
        setIsBlocked(
          sellerRes.data.isBlock
        );
  
        setAccessChecked(true);
      } catch (err) {
        console.error(err);
        setAccessChecked(true);
      }
    };
  
    checkAccess();
  }, []);

  const [canCreate, setCanCreate] = useState<boolean>(true);

  const checkTicketLimit = async () => {
    try {
      const res = await ticketAPI.checkLimit({ ticketsLeft: ticketsLeft });
      if (res.data.response === 'S') {
        setCanCreate(res.data.canCreate);
        if (!res.data.canCreate && res.data.nextAllowedTime) {
           setNextAllowedTime(new Date(res.data.nextAllowedTime));
           setCountdown('Calculating...');
        } else if (res.data.canCreate) {
           setNextAllowedTime(null);
           setCountdown(null);
        }
      }
    } catch (error) {
      console.error("Failed to check ticket limit", error);
    }
  };

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const res = await ticketAPI.getList({ 
        status: statusFilter, 
        search: searchQuery,
        page: currentPage,
        limit: itemsPerPage,
        ticketsLeft: ticketsLeft,
        sortBy,
        sortOrder
      });
      if (res.data.response === 'S') {
        setTickets(res.data.data || []);
        if (res.data.pagination) {
          setTotalPages(res.data.pagination.pages);
          setTotalItems(res.data.pagination.total);
        }
        if (res.data.nextAllowedTime) {
          setNextAllowedTime(new Date(res.data.nextAllowedTime));
          setCountdown('Calculating...');
        } else {
          setNextAllowedTime(null);
          setCountdown(null);
        }
        const count = res.data.recentTicketsCount || 0;
        // Do not mutate ticketsLeft with subtraction here, as ticketsLeft represents the MAX quota (10). 
      } else {
        toast.error(res.data.message);
      }
    } catch (err: any) {
      console.error(err);
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!accessChecked) return;

    if (
      isBlocked ||
      verificationStatus === "P" ||
      verificationStatus === "R"
    ) {
      return;
    }

    checkTicketLimit();
    fetchTickets();
  }, [statusFilter, currentPage, itemsPerPage, searchQuery, sortBy, sortOrder, accessChecked, isBlocked, verificationStatus]);

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, searchQuery, itemsPerPage]);

  const getPaginationGroup = () => {
    const pages: (number | string)[] = [];
    const maxPages = Number(totalPages) || 1;
    const currPage = Number(currentPage) || 1;

    if (maxPages <= 8) {
      for (let i = 1; i <= maxPages; i++) pages.push(i);
    } else {
      const lb = 2, rb = 2;
      let sp = Math.max(lb + 1, currPage - 1);
      let ep = Math.min(maxPages - rb, currPage + 1);

      if (currPage <= lb + 2) ep = lb + 3;
      if (currPage >= maxPages - rb - 1) sp = maxPages - rb - 2;

      for (let i = 1; i <= lb; i++) pages.push(i);
      if (sp > lb + 1) pages.push("...");
      for (let i = sp; i <= ep; i++) pages.push(i);
      if (ep < maxPages - rb) pages.push("...");
      for (let i = maxPages - rb + 1; i <= maxPages; i++) pages.push(i);
    }
    return pages;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Open': return '#3b82f6';
      case 'Inprogress': return '#f59e0b';
      case 'Under Review': return '#eab308';
      case 'More Information Required': return '#f97316';
      case 'Approved': return '#10b981';
      case 'Resolved': return '#10b981';
      case 'Rejected': return '#ef4444';
      case 'Completed': return '#64748b';
      case 'Closed': return '#475569';
      default: return '#94a3b8';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Open': return <Clock size={14} />;
      case 'Inprogress': return <Clock size={14} />;
      case 'Under Review': return <Eye size={14} />;
      case 'More Information Required': return <AlertCircle size={14} />;
      case 'Approved': return <CheckCircle size={14} />;
      case 'Resolved': return <CheckCircle size={14} />;
      case 'Rejected': return <XCircle size={14} />;
      case 'Completed': return <CheckCircle size={14} />;
      case 'Closed': return <XCircle size={14} />;
      default: return <Clock size={14} />;
    }
  };

  const getCategoryIcon = (category: string) => {
    const getProps = () => {
      switch(category) {
        case 'Account Issue': return { icon: UserCircle, color: '#3b82f6', bg: '#eff6ff' };
        case 'Payment Issue': return { icon: CreditCard, color: '#8b5cf6', bg: '#f5f3ff' };
        case 'Order Issue': return { icon: ShoppingBag, color: '#f59e0b', bg: '#fffbeb' };
        case 'Product Issue': return { icon: Package, color: '#10b981', bg: '#ecfdf5' };
        case 'Technical Support': return { icon: Monitor, color: '#6366f1', bg: '#eef2ff' };
        case 'Delivery Issue': return { icon: Truck, color: '#f97316', bg: '#fff7ed' };
        case 'Seller Support': return { icon: ShieldAlert, color: '#ec4899', bg: '#fdf2f8' };
        case 'Service Request': return { icon: Wrench, color: '#14b8a6', bg: '#f0fdfa' };
        case 'Complaint': return { icon: AlertOctagon, color: '#ef4444', bg: '#fef2f2' };
        case 'Feedback': return { icon: MessageSquare, color: '#84cc16', bg: '#f7fee7' };
        case 'Add Product': return { icon: PackagePlus, color: '#06b6d4', bg: '#ecfeff' };
        default: return { icon: HelpCircle, color: '#64748b', bg: '#f8fafc' };
      }
    };
    const { icon: Icon, color, bg } = getProps();
    return (
      <div style={{ 
        width: '28px', height: '28px', borderRadius: '8px', background: bg, 
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
      }}>
        <Icon size={15} color={color} />
      </div>
    );
  };

  const [exporting, setExporting] = useState(false);

  const fetchExportData = async () => {
    const res = await ticketAPI.getList({ 
      status: statusFilter, 
      search: searchQuery,
      page: 1,
      limit: 9999,
      sortBy,
      sortOrder
    });
    if (res.data.response === 'S') {
      return res.data.data || [];
    }
    return [];
  };

  const exportToPDF = async () => {
    try {
      setExporting(true);
      const dataToExport = await fetchExportData();

      if (dataToExport.length === 0) {
        Swal.fire({ title: 'No Data', text: 'There is no data to export.', icon: 'info', confirmButtonColor: '#14b8a6' });
        return;
      }

      const doc = new jsPDF();
      doc.setFont("Helvetica", "bold");
      doc.setFontSize(20);
      doc.setTextColor(20, 184, 166);
      doc.text("DECSOPLUS STORE", 14, 15);

      doc.setFontSize(10);
      doc.setFont("Helvetica", "normal");
      doc.setTextColor(100, 116, 139);
      doc.text("Tickets Report", 14, 21);

      const dateStr = new Date().toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
      doc.text(`Generated: ${dateStr}`, 196, 15, { align: 'right' });

      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.5);
      doc.line(14, 25, 196, 25);

      doc.setFontSize(9);
      doc.setFont("Helvetica", "bold");
      doc.setTextColor(15, 23, 42);
      doc.text(`Total Tickets: ${dataToExport.length}`, 14, 31);

      const tableHeaders = [["S.No", "Ticket ID", "Category", "Subject", "Priority", "Status", "Date"]];
      const tableRows = dataToExport.map((t: any, index: number) => [
        (index + 1).toString(),
        t.ticketId || '—',
        t.category || '—',
        t.subject || '—',
        t.priority || '—',
        t.status || '—',
        new Date(t.createdAt).toLocaleDateString()
      ]);

      autoTable(doc, {
        startY: 35,
        head: tableHeaders,
        body: tableRows,
        theme: 'striped',
        headStyles: { fillColor: [20, 184, 166], textColor: 255, fontSize: 9, fontStyle: 'bold', halign: 'left' },
        bodyStyles: { fontSize: 8, textColor: [51, 65, 85], valign: 'middle' },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        didDrawPage: (data: any) => {
          const pageCount = doc.getNumberOfPages();
          doc.setFontSize(8);
          doc.setFont("Helvetica", "normal");
          doc.setTextColor(148, 163, 184);
          doc.text(`Page ${data.pageNumber} of ${pageCount}`, data.settings.margin.left, doc.internal.pageSize.height - 10);
          doc.text("DecsoPlus © All Rights Reserved", doc.internal.pageSize.width - data.settings.margin.right, doc.internal.pageSize.height - 10, { align: 'right' });
        },
        margin: { top: 35, bottom: 15, left: 14, right: 14 }
      });

      doc.save(`DecsoPlus_Tickets_${new Date().toISOString().slice(0, 10)}.pdf`);
      Swal.fire({ title: 'Success!', text: 'Tickets exported to PDF successfully.', icon: 'success', confirmButtonColor: '#14b8a6' });
    } catch (err: any) {
      Swal.fire({ title: 'Export Failed', text: err.message || 'Something went wrong.', icon: 'error', confirmButtonColor: '#14b8a6' });
    } finally {
      setExporting(false);
    }
  };

  const exportToCSV = async () => {
    try {
      setExporting(true);
      const dataToExport = await fetchExportData();

      if (dataToExport.length === 0) {
        Swal.fire({ title: 'No Data', text: 'There is no data to export.', icon: 'info', confirmButtonColor: '#14b8a6' });
        return;
      }

      const headers = ["S.No", "Ticket ID", "Category", "Subject", "Priority", "Status", "Date"];
      const rows = dataToExport.map((t: any, index: number) => [
        index + 1,
        `"${(t.ticketId || '').replace(/"/g, '""')}"`,
        `"${(t.category || '').replace(/"/g, '""')}"`,
        `"${(t.subject || '').replace(/"/g, '""')}"`,
        `"${(t.priority || '').replace(/"/g, '""')}"`,
        `"${(t.status || '').replace(/"/g, '""')}"`,
        `"${new Date(t.createdAt).toLocaleDateString().replace(/"/g, '""')}"`
      ]);

      const csvContent = [headers.join(","), ...rows.map((r: any) => r.join(","))].join("\n");
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `DecsoPlus_Tickets_${new Date().toISOString().slice(0, 10)}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      Swal.fire({ title: 'Success!', text: 'Tickets exported to CSV successfully.', icon: 'success', confirmButtonColor: '#14b8a6' });
    } catch (err: any) {
      Swal.fire({ title: 'Export Failed', text: err.message || 'Something went wrong.', icon: 'error', confirmButtonColor: '#14b8a6' });
    } finally {
      setExporting(false);
    }
  };

  const handleExportClick = () => {
    Swal.fire({
      title: 'Export Format',
      text: 'Choose which format you want to export to:',
      icon: 'question',
      showCloseButton: true,
      showCancelButton: false,
      showDenyButton: true,
      confirmButtonText: 'Export as CSV',
      denyButtonText: 'Export as PDF',
      confirmButtonColor: '#10b981',
      denyButtonColor: '#f97316',
      customClass: {
        actions: 'export-swal-actions',
        confirmButton: 'export-swal-btn',
        denyButton: 'export-swal-btn'
      }
    }).then((result) => {
      if (result.isConfirmed) {
        exportToCSV();
      } else if (result.isDenied) {
        exportToPDF();
      }
    });
  };

  // accessChecked block removed to allow skeleton UI (table loading) to show immediately.
  
  if (
    isBlocked ||
    verificationStatus === "P" ||
    verificationStatus === "R"
  ) {
    return (
      <AccessModule
        isBlock={isBlocked}
        verification_status={verificationStatus}
      />
    );
  }

  return (
    <div className="fade-in">
      <div className="page-header" style={{ marginBottom: '20px', display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 className="page-title">Tickets</h1>
          <p className="page-subtitle">Manage your Tickets</p>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
         
          
          {countdown && !canCreate ? (
            <button disabled style={{ borderRadius: '10px', padding: '10px 20px', display: 'flex', gap: '8px', alignItems: 'center', whiteSpace: 'nowrap', background: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0', cursor: 'not-allowed', fontWeight: '600' }}>
              <Clock size={16} /> Next request in {countdown}
            </button>
          ) : canCreate ? (
            <button className="btn-primary" onClick={() => navigate('/tickets/category', { state: { ticketsLeft } })} style={{ borderRadius: '10px', padding: '10px 20px', display: 'flex', gap: '8px', alignItems: 'center', whiteSpace: 'nowrap' }}>
              <Plus size={18} /> New Request
            </button>
          ) : null}
        </div>
      </div>

      <div className="products-card">
        <div className="products-card-header" style={{ display: 'flex', justifyContent: 'space-between', padding: '16px 24px', borderBottom: '1px solid #f1f5f9', flexWrap: 'wrap', gap: '16px', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: '12px', flex: 1, minWidth: '300px', flexWrap: 'wrap' }}>
            <div style={{ position: 'relative', flex: 1, maxWidth: '350px', minWidth: '220px' }}>
              <Search size={14} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
              <input
                type="text"
                placeholder="Search Ticket ID, Subject, or Category..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value.trimStart())}
                style={{
                  width: '100%',
                  padding: '8px 10px 8px 32px',
                  borderRadius: '9999px',
                  border: '1px solid #cbd5e1',
                  outline: 'none',
                  fontSize: '13px',
                  color: '#334155',
                  background: 'white'
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
              {['all', 'Open', 'Inprogress', 'Resolved', 'Closed'].map((s) => {
                const isActive = statusFilter === s;
                let color = '';
                let label = '';
                if (s === 'all') { color = '#6E6BFA'; label = 'All Status'; }
                else if (s === 'Open') { color = '#3b82f6'; label = 'Open'; }
                else if (s === 'Inprogress') { color = '#f59e0b'; label = 'Inprogress'; }
                else if (s === 'Resolved') { color = '#10b981'; label = 'Resolved'; }
                else if (s === 'Closed') { color = '#64748b'; label = 'Closed'; }

                return (
                  <button
                    key={s}
                    onClick={() => setStatusFilter(s)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '6px 12px',
                      borderRadius: '9999px',
                      border: `1px solid ${color}`,
                      backgroundColor: isActive ? color : 'white',
                      color: isActive ? 'white' : color,
                      fontWeight: '600',
                      fontSize: '13px',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                  >
                    <span
                      style={{
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        backgroundColor: isActive ? 'white' : color,
                      }}
                    />
                    {label}
                  </button>
                );
              })}
              
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: '6px 12px', background: '#f8fafc', border: '1px solid #e2e8f0',
                    borderRadius: '9999px', cursor: 'pointer', transition: 'all 0.2s',
                    color: '#64748b', fontWeight: '600', fontSize: '12px'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = '#334155'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.color = '#64748b'; }}
                >
                  Clear Search
                </button>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button
              onClick={handleExportClick}
              disabled={exporting}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                background: 'white', border: '1px solid #e2e8f0', color: '#475569',
                borderRadius: '10px', padding: '10px 16px', fontWeight: '600', cursor: exporting ? 'not-allowed' : 'pointer', transition: 'all 0.2s', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => { if (!exporting) { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = '#cbd5e1'; } }}
              onMouseLeave={(e) => { if (!exporting) { e.currentTarget.style.background = 'white'; e.currentTarget.style.borderColor = '#e2e8f0'; } }}
            >
              <Download size={16} />
              {exporting ? 'Exporting...' : 'Export'}
            </button>
          </div>
        </div>

        <div className="products-table-container">
          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '400px', gap: '16px' }}>
              <div style={{ width: '36px', height: '36px', border: '3px solid #f1f5f9', borderTop: '3px solid #3b82f6', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
              <span style={{ fontSize: '15px', fontWeight: '500', color: '#475569' }}>Fetching support tickets...</span>
            </div>
          ) : tickets.length === 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '400px', width: '100%', background: '#fff', borderRadius: '12px' }}>
              <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginBottom: '16px' }}>
                <path d="M53.3333 18.6667H29.3333L24 13.3333H10.6667C7.72 13.3333 5.33333 15.72 5.33333 18.6667V45.3333C5.33333 48.28 7.72 50.6667 10.6667 50.6667H53.3333C56.28 50.6667 58.6667 48.28 58.6667 45.3333V24C58.6667 21.0533 56.28 18.6667 53.3333 18.6667Z" fill="url(#ticket-folder-grad)"/>
                <defs>
                  <linearGradient id="ticket-folder-grad" x1="5.33333" y1="13.3333" x2="58.6667" y2="50.6667" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#FCD34D"/>
                    <stop offset="1" stopColor="#F59E0B"/>
                  </linearGradient>
                </defs>
              </svg>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1e293b', marginBottom: '8px', fontFamily: 'inherit' }}>
                No tickets found
              </h3>
              <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '24px', maxWidth: '400px', textAlign: 'center' }}>
                No records match the selected category, dates, or search term.
              </p>
              {(searchQuery || statusFilter !== 'all') && (
                <button
                  onClick={() => { setSearchQuery(''); setStatusFilter('all'); }}
                  style={{ background: '#2563eb', color: '#ffffff', border: 'none', padding: '10px 24px', borderRadius: '6px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', transition: 'background 0.2s', boxShadow: '0 4px 12px rgba(37, 99, 235, 0.2)' }}
                  onMouseOver={(e) => e.currentTarget.style.background = '#1d4ed8'}
                  onMouseOut={(e) => e.currentTarget.style.background = '#2563eb'}
                >
                  Clear All Filters
                </button>
              )}
            </div>
          ) : (
            <table style={{ margin: 0, width: '100%' }}>
              <thead>
                <tr>
                  <th style={{ paddingLeft: '24px', width: '60px' }}>S.No</th>
                  <th onClick={() => handleSort('ticketId')} style={{ cursor: 'pointer' }}>Ticket ID {sortBy === 'ticketId' ? (sortOrder === 'asc' ? <ArrowUp size={14} style={{display:'inline', verticalAlign:'middle'}}/> : <ArrowDown size={14} style={{display:'inline', verticalAlign:'middle'}}/>) : ''}</th>
                  <th onClick={() => handleSort('category')} style={{ cursor: 'pointer' }}>Category {sortBy === 'category' ? (sortOrder === 'asc' ? <ArrowUp size={14} style={{display:'inline', verticalAlign:'middle'}}/> : <ArrowDown size={14} style={{display:'inline', verticalAlign:'middle'}}/>) : ''}</th>
                  <th onClick={() => handleSort('subject')} style={{ cursor: 'pointer' }}>Subject {sortBy === 'subject' ? (sortOrder === 'asc' ? <ArrowUp size={14} style={{display:'inline', verticalAlign:'middle'}}/> : <ArrowDown size={14} style={{display:'inline', verticalAlign:'middle'}}/>) : ''}</th>
                  <th onClick={() => handleSort('priority')} style={{ cursor: 'pointer' }}>Priority {sortBy === 'priority' ? (sortOrder === 'asc' ? <ArrowUp size={14} style={{display:'inline', verticalAlign:'middle'}}/> : <ArrowDown size={14} style={{display:'inline', verticalAlign:'middle'}}/>) : ''}</th>
                  <th onClick={() => handleSort('createdAt')} style={{ cursor: 'pointer' }}>Date {sortBy === 'createdAt' ? (sortOrder === 'asc' ? <ArrowUp size={14} style={{display:'inline', verticalAlign:'middle'}}/> : <ArrowDown size={14} style={{display:'inline', verticalAlign:'middle'}}/>) : ''}</th>
                  <th onClick={() => handleSort('status')} style={{ cursor: 'pointer' }}>Status {sortBy === 'status' ? (sortOrder === 'asc' ? <ArrowUp size={14} style={{display:'inline', verticalAlign:'middle'}}/> : <ArrowDown size={14} style={{display:'inline', verticalAlign:'middle'}}/>) : ''}</th>
                  <th style={{ paddingRight: '24px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tickets.map((t: any, index: number) => (
                  <tr key={t._id}>
                    <td style={{ paddingLeft: '24px', color: '#64748b' }}>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                    <td style={{ fontWeight: '600', color: '#475569' }}>{t.ticketId}</td>
                    <td style={{ color: '#475569' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {getCategoryIcon(t.category)}
                        <span>{t.category}</span>
                      </div>
                    </td>
                    <td>
                      <div title={t.subject} style={{ fontWeight: '700', color: '#0f172a', cursor: 'pointer' }}>
                        {t.subject?.length > 30 ? t.subject.substring(0, 30) + '...' : t.subject}
                      </div>
                      <div style={{ fontSize: '13px', color: '#64748b' }}>{t.subCategory}</div>
                    </td>
                    <td style={{ color: '#475569' }}>
                      <span style={{ 
                        padding: '2px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: '600',
                        background: t.priority === 'Critical' ? '#fee2e2' : t.priority === 'High' ? '#ffedd5' : t.priority === 'Medium' ? '#e0f2fe' : '#f1f5f9',
                        color: t.priority === 'Critical' ? '#991b1b' : t.priority === 'High' ? '#9a3412' : t.priority === 'Medium' ? '#075985' : '#475569'
                      }}>
                        {t.priority || 'Medium'}
                      </span>
                    </td>
                    <td style={{ color: '#475569' }}>
                      <div style={{ fontSize: '13px' }}>{new Date(t.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '-')}</div>
                      <div style={{ fontSize: '12px', color: '#94a3b8' }}>{new Date(t.createdAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}</div>
                    </td>
                    <td>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: '6px',
                        padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600',
                        background: `${getStatusColor(t.ticketstatus)}15`, color: getStatusColor(t.ticketstatus)
                      }}>
                        {getStatusIcon(t.ticketstatus)} {t.ticketstatus}
                      </span>
                    </td>
                    <td style={{ paddingRight: '24px' }}>
                      <button onClick={() => navigate(`/tickets/${t._id}`)}
                        style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', color: '#2563eb' }}
                        title="View Details">
                        <Eye size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        
          <div className="products-card-footer">
            <div style={{ display: 'flex', alignItems: 'center', gap: '20px', fontSize: '13px', color: '#64748b' }}>
              <span>Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} entries</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>Per page:</span>
                <select 
                  value={itemsPerPage} 
                  onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                  style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #cbd5e1', outline: 'none', cursor: 'pointer', background: 'white', color: '#334155' }}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <button 
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))} 
                disabled={Number(currentPage) <= 1}
                style={{ 
                  padding: '8px', borderRadius: '8px', border: '1px solid #e2e8f0', background: 'white', 
                  cursor: Number(currentPage) <= 1 ? 'not-allowed' : 'pointer', opacity: Number(currentPage) <= 1 ? 0.5 : 1, 
                  display: 'flex', alignItems: 'center', color: '#475569', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', transition: 'all 0.2s' 
                }}
                onMouseEnter={(e) => { if (Number(currentPage) > 1) e.currentTarget.style.background = '#f8fafc'; }}
                onMouseLeave={(e) => { if (Number(currentPage) > 1) e.currentTarget.style.background = 'white'; }}
                title="Previous page"
              >
                <ChevronLeft size={16} />
              </button>

              <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                {getPaginationGroup().map((pg, i) => {
                  if (pg === "...") {
                    return (
                      <span
                        key={`ellipsis-${i}`}
                        style={{
                          width: '30px',
                          height: '34px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#94a3b8',
                          fontWeight: '700',
                          fontSize: '14px',
                          userSelect: 'none'
                        }}
                      >
                        …
                      </span>
                    );
                  }

                  const pageNum = Number(pg);
                  const isActive = Number(currentPage) === pageNum;

                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      style={{
                        width: '34px',
                        height: '34px',
                        borderRadius: '8px',
                        border: '1px solid',
                        borderColor: isActive ? '#14b8a6' : '#e2e8f0',
                        background: isActive ? '#14b8a6' : 'white',
                        color: isActive ? 'white' : '#475569',
                        fontWeight: isActive ? '700' : '600',
                        cursor: 'pointer',
                        fontSize: '13px',
                        transition: 'all 0.2s',
                        boxShadow: isActive ? '0 4px 12px rgba(20,184,166,0.2)' : '0 1px 2px rgba(0,0,0,0.05)'
                      }}
                      onMouseEnter={(e) => {
                        if (!isActive) {
                          e.currentTarget.style.borderColor = '#cbd5e1';
                          e.currentTarget.style.background = '#f8fafc';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isActive) {
                          e.currentTarget.style.borderColor = '#e2e8f0';
                          e.currentTarget.style.background = 'white';
                        }
                      }}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button 
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} 
                disabled={Number(currentPage) >= (Number(totalPages) || 1)}
                style={{ 
                  padding: '8px', borderRadius: '8px', border: '1px solid #e2e8f0', background: 'white', 
                  cursor: Number(currentPage) >= (Number(totalPages) || 1) ? 'not-allowed' : 'pointer', 
                  opacity: Number(currentPage) >= (Number(totalPages) || 1) ? 0.5 : 1, 
                  display: 'flex', alignItems: 'center', color: '#475569', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', transition: 'all 0.2s' 
                }}
                onMouseEnter={(e) => { if (Number(currentPage) < (Number(totalPages) || 1)) e.currentTarget.style.background = '#f8fafc'; }}
                onMouseLeave={(e) => { if (Number(currentPage) < (Number(totalPages) || 1)) e.currentTarget.style.background = 'white'; }}
                title="Next page"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
      </div>
    </div>
  );
}
